import React, {
    useRef,
    useEffect,
    useState,
    useReducer,
    Fragment,
    ReactElement,
} from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';

import mapboxgl from '!mapbox-gl';

import { Map as TMapboxMap } from '!mapbox-gl';

import { LAYER_SOURCE_NAMES } from '@constants';

import {
    TGlobalIncidentsMap,
    TInitialState,
    TAction,
} from './global-incidents-map.types';
import {
    initMap,
    aggregateCountryIncidentData,
    updateIncidentLayers,
    setBoundsToCountry,
    setCountryStyles,
    updateCountriesLayer,
    getFilterColumn,
    setVisibleIncidentsInMap,
    loadCountriesGeoJSON,
} from './global-incidents-map.logic';

import {
    StyledMapProgress,
    StyledMapContainer,
} from './global-incidents-map.styled';
import {
    closePopups,
    filterIncidents,
    setMapViewFromParameters,
    updateUrlParameters,
} from '@helpers';

import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { resetStatisticsScroll } from 'components/StatisticsContainer';

const INITIAL_STATE: TInitialState = {
    countryCode: '',
    isMapLoading: true,
    filteredIncidents: [],
};

function reducer(state: TInitialState, action: TAction) {
    switch (action.type) {
        case 'updateMapLoadingState':
            return { ...state, isMapLoading: action.value };
        case 'setSelectedCountry':
            return { ...state, countryCode: action.value };
        case 'setFilteredIncidents':
            return { ...state, filteredIncidents: action.value };
        case 'clearSelectedCountry':
            return { ...state, countryCode: '' };
        default:
            return INITIAL_STATE;
    }
}

export function GlobalIncidentsMap({
    filter,
    incidents,
    selectedCountry,
    setSelectedCountry,
    clearSelectedCountry,
    setFilteredStatistics,
    selectedAreaNavigation,
    scrollZoomRequiresFocus,
    setMultipleCountries,
}: TGlobalIncidentsMap): ReactElement {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    const mapReference = useRef<HTMLDivElement>(null);
    const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const hadCountrySelected = useRef(false);
    const currentSelectedCountry = useRef('');

    const [map, setMap] = useState<TMapboxMap>();

    function handleSelectCountry(countryCode: string): void {
        dispatch({ type: 'setSelectedCountry', value: countryCode });
    }

    function handleClearSelectedCountry(): void {
        dispatch({ type: 'clearSelectedCountry' });
    }

    useEffect(() => {
        loadCountriesGeoJSON();
    }, []);

    useEffect(() => {
        if (!selectedCountry) dispatch({ type: 'clearSelectedCountry' });
    }, [selectedCountry]);

    useEffect(() => {
        if (!map) return;

        const { filteredIncidents, countryCode } = state;

        // Define function to use async inside useEffect
        const setCountry = async () => {
            try {
                const countryData = await aggregateCountryIncidentData(
                    filteredIncidents,
                    countryCode
                );
                setSelectedCountry(countryData);
            } catch (error) {
                console.error(
                    'Failed to aggregate country incident data:',
                    error
                );
            }
        };

        // Prevent clearing countryCode from URL during map load to preserve URL-based state
        if (!countryCode && hadCountrySelected.current) {
            clearSelectedCountry();

            const currentZoom = map.getZoom();
            map.easeTo({
                zoom: Math.max(currentZoom - 1, 1.5),
                duration: 600,
            });
        } else if (countryCode) {
            setCountry().catch(console.error);
            updateUrlParameters({ country: countryCode });
        }

        if (currentSelectedCountry.current != countryCode) {
            setCountryStyles(
                map,
                filter.category,
                filteredIncidents,
                countryCode
            );
            setVisibleIncidentsInMap(map, countryCode);
            resetStatisticsScroll();
            currentSelectedCountry.current = countryCode;
        }
        hadCountrySelected.current = countryCode ? true : false;
    }, [state]);

    useEffect(() => {
        if (!incidents || !map) {
            return;
        }

        // Cleanup function abort flag
        let isCancelled = false;

        const applyFilter = () => {
            // Cancel queued updates
            if (isCancelled) {
                return;
            }
            const { countryCode } = state;

            const filterColumn = getFilterColumn(filter.category);
            const filteredIncidents = filterIncidents(
                incidents,
                filter,
                filterColumn
            );

            dispatch({
                type: 'setFilteredIncidents',
                value: filteredIncidents,
            });

            resetStatisticsScroll();
            if (!selectedCountry) {
                setCountryStyles(map, filter.category, filteredIncidents);
                setVisibleIncidentsInMap(map);
            } else
                dispatch({
                    type: 'setSelectedCountry',
                    value: countryCode,
                });
        };

        if (!map?.isStyleLoaded()) {
            map.once('idle', applyFilter);
        } else {
            applyFilter();
        }

        // Cancels any pending idle callback from this run
        return () => {
            isCancelled = true;
        };
    }, [incidents, filter]);

    useEffect(() => {
        if (
            !incidents ||
            !map ||
            !map.getSource(LAYER_SOURCE_NAMES.COUNTRIES)
        ) {
            return;
        }
        closePopups();
        const { filteredIncidents } = state;
        // Define function to use async inside useEffect
        const updateIncidents = async () => {
            await updateCountriesLayer(map, filteredIncidents);
            setFilteredStatistics(filteredIncidents);
            updateIncidentLayers(map, filteredIncidents);
        };

        updateIncidents().catch(console.error);
    }, [state.filteredIncidents]);

    useEffect(() => {
        // Define function to use async inside useEffect
        const startMap = async () => {
            await loadCountriesGeoJSON();
            if (mapReference.current && incidents) {
                const filterColumn = getFilterColumn(filter.category);
                const filteredIncidents = filterIncidents(
                    incidents,
                    filter,
                    filterColumn
                );

                const mapboxMapObject = initMap(
                    mapReference.current,
                    filteredIncidents,
                    filter.category,
                    handleSelectCountry,
                    handleClearSelectedCountry,
                    scrollZoomRequiresFocus,
                    isSmallScreen,
                    setMultipleCountries
                );

                dispatch({
                    type: 'setFilteredIncidents',
                    value: filteredIncidents,
                });
                setFilteredStatistics(filteredIncidents);
                dispatch({ type: 'updateMapLoadingState', value: false });
                setMap(mapboxMapObject);
            }
        };

        startMap().catch(console.error);
    }, [incidents]);

    useEffect(() => {
        if (!selectedAreaNavigation || !map) {
            return;
        }
        const { isoCountryCode, mapLngLat, mapZoom } = selectedAreaNavigation;
        if (!isoCountryCode && !mapLngLat && !mapZoom) {
            return;
        }
        const hasMapView = mapZoom && mapLngLat;
        if (hasMapView) {
            setMapViewFromParameters(map, mapLngLat, mapZoom);
        }
        if (isoCountryCode) {
            if (!hasMapView) {
                setBoundsToCountry(map, isoCountryCode, isSmallScreen);
            }
            dispatch({ type: 'setSelectedCountry', value: isoCountryCode });
        }
    }, [selectedAreaNavigation]);

    return (
        <Fragment>
            {state.isMapLoading && (
                <StyledMapProgress>
                    <CircularProgress color="inherit" />
                </StyledMapProgress>
            )}
            <StyledMapContainer id="global-map" ref={mapReference} />
        </Fragment>
    );
}
